import React, { useState, useEffect } from 'react';
import { X, ScanLine, Pencil } from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';
import NutritionLabelPhotoScan from './NutritionLabelPhotoScan';
import { lookupBarcode, confirmAddedFood, getVegSpecies, setFoodClassification } from '../api';

// Lets ANY role — chef, admin, or player — add a brand-new product to the shared food database,
// two ways: scan a barcode (reusing the exact same Open Food Facts lookup + Claude-vision
// label-photo fallback already built for the barcode-miss flow — BarcodeScanner, lookupBarcode,
// NutritionLabelPhotoScan, all unchanged) or go straight to manual entry when there's no barcode
// at all (NutritionLabelPhotoScan reused again, just with barcode=null). Either way lands in the
// same central nutrition_foods table (source='chef' or 'player', set server-side from the
// caller's own session role — never trust-the-client) and shows up in FoodAutocomplete for
// everyone immediately, like any NEVO/OFF product — never a half-added or per-user-only row.
// `showClassify` (chef/admin flows only — the Groentesoort/Eiwitcategorie step means nothing to
// a player) defaults to true; PlayerNutrition.js passes false to skip straight to onAdded.
const PROTEIN_TYPE_OPTIONS = [
  { key: 'vis', label: 'Vis' },
  { key: 'tofu_tempeh', label: 'Tofu/tempeh' },
  { key: 'rood_vlees', label: 'Rood vlees' },
  { key: 'kip', label: 'Kip' },
];

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const box = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 380, maxHeight: '90vh', overflowY: 'auto' };
const inputStyle = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const label = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
const btn = (active, color = '#3b82f6') => ({
  padding: '8px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
});

// onDoneAndNext is only passed when the caller supports rapid multi-add ("in een rits") — its
// own button only renders then, so a caller that doesn't wire it up (there's currently only one)
// just gets the original two-button layout.
function ClassifyStep({ food, onDone, onDoneAndNext }) {
  const [species, setSpecies] = useState([]);
  const [speciesId, setSpeciesId] = useState('');
  const [proteinType, setProteinType] = useState('');
  const [saving, setSaving] = useState(null); // null | 'done' | 'next' — which button is in flight
  const [err, setErr] = useState('');

  useEffect(() => { getVegSpecies().then(setSpecies).catch(() => setSpecies([])); }, []);

  const save = async (andNext) => {
    setSaving(andNext ? 'next' : 'done'); setErr('');
    try {
      await setFoodClassification(food.id, { species_id: speciesId ? Number(speciesId) : null, protein_type: proteinType || null });
      if (andNext) onDoneAndNext(); else onDone();
    } catch (e) { setErr(e.message || 'Opslaan mislukt.'); } finally { setSaving(null); }
  };

  const nonExcluded = species.filter(s => !s.excluded);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{food.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>
        Product toegevoegd. Indelen voor de richtlijnen-check (optioneel — kan later ook via het indeling-overzicht).
      </div>
      <span style={label}>Groentesoort</span>
      <select value={speciesId} onChange={e => setSpeciesId(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
        <option value="">— geen —</option>
        {nonExcluded.map(s => <option key={s.id} value={s.id}>{s.name} ({s.family})</option>)}
      </select>
      <span style={label}>Eiwitcategorie</span>
      <select value={proteinType} onChange={e => setProteinType(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
        <option value="">— geen —</option>
        {PROTEIN_TYPE_OPTIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {err && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={btn(true)} onClick={() => save(false)} disabled={!!saving}>{saving === 'done' ? 'Saving…' : 'Opslaan'}</button>
        {onDoneAndNext && (
          <button style={btn(false)} onClick={() => save(true)} disabled={!!saving}>
            {saving === 'next' ? 'Saving…' : 'Opslaan + volgende toevoegen'}
          </button>
        )}
        <button style={btn(false)} onClick={onDone} disabled={!!saving}>Overslaan</button>
      </div>
    </div>
  );
}

// onAddedKeepOpen, when passed, enables "in een rits" rapid multi-add: ClassifyStep's extra
// button saves the just-added product THE SAME WAY as normal (onAddedKeepOpen), but instead of
// the caller closing the modal (like the plain onAdded path does), this component resets its own
// state back to the barcode/manual choice so the chef can immediately add the next product
// without leaving the modal or navigating back to another screen. Every product added this way
// always lands as a plain new ingredient (there's no "next specific row" to target once the
// modal was opened to fix one particular unmatched row) — addedCount just tracks how many for
// the header tally.
export default function AddFoodModal({ onAdded, onAddedKeepOpen, onClose, showClassify = true }) {
  const [step, setStep] = useState('choose'); // choose | scanning | looking-up | photo | classify
  const [barcode, setBarcode] = useState(null);
  const [lookupMsg, setLookupMsg] = useState('');
  const [food, setFood] = useState(null);
  const [addedCount, setAddedCount] = useState(0);

  const onScanned = async (code) => {
    setStep('looking-up'); setLookupMsg('');
    try {
      const r = await lookupBarcode(code);
      if (r.ok && r.food) { onPhotoSaved(r.food); return; }
      if (r.status === 429) {
        setLookupMsg('Barcode-service is druk — probeer het zo weer, of vul handmatig in.');
        setStep('choose');
        return;
      }
      // not_found / incomplete — offer the photo/manual fallback for this specific barcode.
      setBarcode(code);
      setStep('photo');
    } catch {
      setLookupMsg('Opzoeken mislukt — probeer opnieuw, of vul handmatig in.');
      setStep('choose');
    }
  };

  const startManual = () => { setBarcode(null); setLookupMsg(''); setStep('photo'); };
  const onPhotoSaved = (savedFood) => {
    if (!showClassify) { setFood(savedFood); onAdded(savedFood); return; }
    setFood(savedFood); setStep('classify');
  };
  const finish = () => onAdded(food);
  const finishAndAddAnother = () => {
    onAddedKeepOpen(food);
    setAddedCount(c => c + 1);
    setFood(null); setBarcode(null); setLookupMsg('');
    setStep('choose');
  };

  if (step === 'scanning') {
    return <BarcodeScanner onDetect={onScanned} onClose={() => setStep('choose')} />;
  }
  if (step === 'photo') {
    return (
      <NutritionLabelPhotoScan
        barcode={barcode}
        confirmFn={confirmAddedFood}
        title={barcode ? 'Product niet gevonden' : 'Nieuw product'}
        introText={barcode
          ? "Deze barcode is niet gevonden in Open Food Facts. Maak een foto van de voedingswaardetabel, of vul de waarden handmatig in."
          : 'Vul de voedingswaardetabel in, of maak een foto van het etiket.'}
        onClose={() => setStep('choose')}
        onSaved={onPhotoSaved}
      />
    );
  }

  return (
    <div style={overlay} onClick={step === 'looking-up' ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={box}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>
            Nieuw product toevoegen{addedCount > 0 ? ` (${addedCount} toegevoegd)` : ''}
          </span>
          <button onClick={step === 'classify' ? finish : onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>

        {step === 'choose' && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lookupMsg && <div style={{ fontSize: 12, color: '#f59e0b' }}>{lookupMsg}</div>}
            <button style={btn(true)} onClick={() => setStep('scanning')}><ScanLine size={14} /> Scan barcode</button>
            <button style={btn(false)} onClick={startManual}><Pencil size={14} /> Handmatig toevoegen</button>
            {addedCount > 0 && <button style={{ ...btn(false), border: 'none' }} onClick={onClose}>Klaar, sluiten</button>}
          </div>
        )}
        {step === 'looking-up' && (
          <div style={{ padding: 30, textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>Barcode opzoeken…</div>
        )}
        {step === 'classify' && food && (
          <ClassifyStep food={food} onDone={finish} onDoneAndNext={onAddedKeepOpen ? finishAndAddAnother : undefined} />
        )}
      </div>
    </div>
  );
}
